import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { IAccountInterface } from './account.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountState } from './account.state';
import { AccountApi } from './account.api';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class AccountService {
	private readonly SP_ROUTING_PERMISSIONS = [
		'organizations:InviteAccountToOrganization',
		'organizations:DeclineHandshake',
		'organizations:AcceptHandshake',
		'organizations:CancelHandshake',
		'organizations:MoveAccount',
	];
	private cache$: Observable<IAccountInterface[]>;

	constructor(private accountApi: AccountApi, private accountState: AccountState) {}

	isUpdating$(): Observable<boolean> {
		return this.accountState.isUpdating$();
	}

	initAccounts(data: IAccountInterface[]): void {
		this.accountState.setUpdating(true);

		this.cache$ = of(data).pipe(
			map((accounts) => this.setAccountPermissionsStatus(accounts)),
			tap((accounts) => {
				this.accountState.setAccounts(accounts);
				this.accountState.setUpdating(false);
			}),
			shareReplay(1),
		);
	}

	loadAccounts(): Observable<IAccountInterface[]> {
		if (this.cache$) {
			return this.cache$;
		}
		this.accountState.setUpdating(true);

		const data$ = this.accountApi.getAll().pipe(
			map((accounts) => this.setAccountPermissionsStatus(accounts)),
			tap((accounts) => {
				this.accountState.setAccounts(accounts);
				this.accountState.setUpdating(false);
			}),
			shareReplay(1),
		);

		this.cache$ = data$;

		return data$;
	}

	getAccounts(): Observable<IAccountInterface[]> {
		return this.accountState.getAccounts();
	}

	getDisabledAccounts(): Observable<IAccountInterface[]> {
		return this.accountState
			.getAccounts()
			.pipe(map((accounts) => accounts.filter((account) => account.zd_enabled === false)));
	}

	getAccountsSnapshot(): IAccountInterface[] {
		return this.accountState.getAccountsValue();
	}

	renameAccount(updatedAccount: IAccountInterface): Promise<boolean> {
		this.accountState.setUpdating(true);

		return new Promise((resolve) => {
			this.accountApi
				.renameAccount(updatedAccount)
				.pipe(finalize(() => this.accountState.setUpdating(false)))
				.subscribe({
					next: (res) => {
						if (res instanceof HttpErrorResponse) {
							resolve(false);
							return;
						}

						this.accountState.renameAccount(updatedAccount);
						resolve(true);
					},
				});
		});
	}

	private accountNoAccess(account: IAccountInterface): boolean {
		return account.status === 0;
	}

	private accountReadOnly(account: IAccountInterface): boolean {
		return account.status === 1 && account.permissions && !account.permissions['ec2:PurchaseReservedInstancesOffering'];
	}

	private accountActive(account: IAccountInterface): boolean {
		return (
			account.status === 1 &&
			account.permissions &&
			(account.permissions['ec2:PurchaseReservedInstancesOffering'] ||
				this.SP_ROUTING_PERMISSIONS.every((item) => account.permissions[item]))
		);
	}

	private setAccountPermissionsStatus(accounts: IAccountInterface[]): IAccountInterface[] {
		return accounts.map((account) => {
			account.permissionsStatus = this.accountNoAccess(account)
				? 'No access'
				: this.accountActive(account)
				? 'Active'
				: this.accountReadOnly(account)
				? 'Read-only'
				: '';

			return account;
		});
	}
}
